// src/features/dashboard/teacher/ImportTopicsModal.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../hooks/useAuth';
import {
  fetchTeachersForSubjectGrade,
  fetchTopicsForClass,
  addTopic,
} from '../../../services/sheetDataService';
import Modal from '../../../components/ui/Modal';

const ImportTopicsModal = ({ isOpen, onClose, classItem, spreadsheetId, onImportComplete }) => {
  const { accessToken } = useAuth();
  const [teachers, setTeachers] = useState([]);
  const [selectedTeacher, setSelectedTeacher] = useState('');
  const [loading, setLoading] = useState(false);
  const [sourceTopics, setSourceTopics] = useState([]);
  const [selectedTopics, setSelectedTopics] = useState({});
  const [importing, setImporting] = useState(false);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen && classItem) {
      setSelectedTeacher('');
      setSourceTopics([]);
      setSelectedTopics({});
      setLoading(true);
      const loadTeachers = async () => {
        try {
          const list = await fetchTeachersForSubjectGrade(
            accessToken,
            spreadsheetId,
            classItem.subject,
            classItem.grade,
            classItem.email
          );
          setTeachers(list);
        } catch (err) {
          console.error('Failed to load teachers:', err);
        } finally {
          setLoading(false);
        }
      };
      loadTeachers();
    }
  }, [isOpen, classItem, spreadsheetId, accessToken]);

  const handleTeacherSelect = async (e) => {
    const teacher = e.target.value;
    setSelectedTeacher(teacher);
    if (!teacher) {
      setSourceTopics([]);
      return;
    }
    setLoading(true);
    try {
      const topics = await fetchTopicsForClass(
        accessToken,
        spreadsheetId,
        teacher,
        classItem.subject,
        classItem.grade
      );
      setSourceTopics(topics);
      // Initially select all topics
      const initial = {};
      topics.forEach(t => { initial[t.rowNumber] = true; });
      setSelectedTopics(initial);
    } catch (err) {
      console.error('Failed to load topics:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleAll = (checked) => {
    const newSel = {};
    sourceTopics.forEach(t => { newSel[t.rowNumber] = checked; });
    setSelectedTopics(newSel);
  };

  const toggleTopic = (rowNumber) => {
    setSelectedTopics(prev => ({ ...prev, [rowNumber]: !prev[rowNumber] }));
  };

  const handleImport = async () => {
    const topicsToCopy = sourceTopics.filter(t => selectedTopics[t.rowNumber]);
    if (topicsToCopy.length === 0) {
      alert('Please select at least one topic to import.');
      return;
    }

    setImporting(true);
    try {
      // Fetch existing topics for current class to check duplicates
      const existing = await fetchTopicsForClass(
        accessToken,
        spreadsheetId,
        classItem.email,
        classItem.subject,
        classItem.grade
      );
      const existingKeys = new Set();
      existing.forEach(t => {
        const key = `${t.topic.trim().toLowerCase()}|${t.subtopics.trim().toLowerCase()}|${t.term.trim().toLowerCase()}`;
        existingKeys.add(key);
      });

      let added = 0;
      let skipped = 0;

      // Process each selected topic
      for (const topic of topicsToCopy) {
        const key = `${topic.topic.trim().toLowerCase()}|${topic.subtopics.trim().toLowerCase()}|${topic.term.trim().toLowerCase()}`;
        if (existingKeys.has(key)) {
          skipped++;
          continue;
        }

        await addTopic(accessToken, spreadsheetId, {
          teacher: classItem.email,
          subject: classItem.subject,
          grade: classItem.grade,
          topic: topic.topic,
          subtopics: topic.subtopics,
          term: topic.term,
          termStart: topic.termStart,
          termEnd: topic.termEnd,
        });
        added++;
      }

      alert(`✅ ${added} topics imported, ${skipped} skipped (duplicates).`);
      onClose();
      if (onImportComplete) onImportComplete();
    } catch (err) {
      alert('❌ Failed to import topics: ' + err.message);
    } finally {
      setImporting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <h2 className="text-2xl font-bold mb-4">Import Topics from Another Teacher</h2>

      <div className="mb-4">
        <label className="block text-sm font-medium mb-1">Select Teacher</label>
        <select
          value={selectedTeacher}
          onChange={handleTeacherSelect}
          className="w-full border rounded px-3 py-2"
          disabled={loading}
        >
          <option value="">-- Choose a teacher --</option>
          {teachers.map(t => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
        {!loading && teachers.length === 0 && (
          <p className="text-sm text-gray-500 mt-1">
            No other teachers have topics for {classItem.subject} ({classItem.grade}).
          </p>
        )}
      </div>

      {selectedTeacher && (
        <div>
          {loading ? (
            <p>Loading topics...</p>
          ) : (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <input
                  type="checkbox"
                  checked={sourceTopics.length > 0 && sourceTopics.every(t => selectedTopics[t.rowNumber])}
                  onChange={(e) => toggleAll(e.target.checked)}
                />
                <label className="text-sm font-medium">Select All ({sourceTopics.length} topics)</label>
              </div>
              <div className="border rounded max-h-60 overflow-y-auto">
                {sourceTopics.map(t => (
                  <div key={t.rowNumber} className="flex items-center gap-2 p-2 border-b hover:bg-gray-50">
                    <input
                      type="checkbox"
                      checked={!!selectedTopics[t.rowNumber]}
                      onChange={() => toggleTopic(t.rowNumber)}
                    />
                    <span className="font-medium">{t.topic}</span>
                    <span className="text-sm text-gray-500">({t.subtopics || 'No subtopics'}) – {t.term}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="flex justify-end gap-2 mt-4">
        <button
          onClick={onClose}
          className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
          disabled={importing}
        >
          Cancel
        </button>
        <button
          onClick={handleImport}
          disabled={!selectedTeacher || importing || sourceTopics.length === 0}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
        >
          {importing ? 'Importing...' : 'Import Selected Topics'}
        </button>
      </div>
    </Modal>
  );
};

export default ImportTopicsModal;